import { FC } from 'react';
import { EAttendanceType, EEventStatus } from 'data/types/events.types';
import { Flex } from 'antd';
import { useEventAccessControl } from 'hooks/useEventAccessControl';
import { useAppSelector } from 'redux/store';
import Title from './Title';
import Type from './Type';
import Host from './Host';
import Location from './Location';
import { TBasicInfoProps } from './BasicInfo.types';
import Providing from './Providing';
import CoordinatorInput from './CoordinatorInput';
import EventCapacity from './EventCapacity';
import MeetingLink from './MeetingLink';
import PartsDate from './PartsDates';
import TimezoneSelector from './TimezoneSelector/TimezoneSelector';
import LanguageSelector from './LanguageSelector/LanguageSelector';
import EventAttendance from './EventAttendance/EventAttendance';
import BadgeWrapper from './BadgeWrapper';

const BasicInfo: FC<TBasicInfoProps> = ({ event, id, canEdit }) => {
  const isVirtual = event?.attendanceType === EAttendanceType.VIRTUAL;

  // Special logic for coordinator field: only owner can edit, or admins
  const usersData = useAppSelector(state => state.userSlice);
  const { isOwner, isAdmin, isSuperAdmin } = useEventAccessControl({
    userTypes: usersData?.user?.type,
    userId: usersData?.user?.userId,
    eventData: event,
  });
  const canEditCoordinator = canEdit && (isOwner || isAdmin);

  // Title/Type editing: creator and admins while in PLANNING or RESCHEDULED
  // (RESCHEDULED behaves like PLANNING per the GROW-3546 ruling), Super Admin
  // in any status except CLOSED/CANCELLED (the backend rejects writes on
  // those statuses for everyone)
  const isClosedOrCancelled =
    event?.status === EEventStatus.CLOSED ||
    event?.status === EEventStatus.CANCELLED;
  const isPlanningPhase =
    event?.status === EEventStatus.PLANNING ||
    event?.status === EEventStatus.RESCHEDULED;
  const canEditTitleAndType =
    !isClosedOrCancelled &&
    (isSuperAdmin || (canEdit && (isOwner || isAdmin) && isPlanningPhase));

  return (
    <BadgeWrapper
      badgeItems={[
        {
          text: 'Virtual Event',
          hide: !isVirtual,
          color: 'purple',
        },
      ]}
    >
      <Flex vertical gap={20}>
        <Title id={id} title={event?.title} readonly={!canEditTitleAndType} />

        <Type
          id={id}
          type={event?.type}
          typeCategory={event?.typeCategory}
          readonly={!canEditTitleAndType}
        />

        <Host id={id} host={event?.host} />

        {isVirtual ? (
          <MeetingLink eventId={id} meetingLink={event?.meetingLink} />
        ) : (
          <Location
            location={event?.location}
            host={event?.host}
            sameAsHost={event?.locationSameAsHost}
            id={id}
          />
        )}

        <CoordinatorInput
          id={id}
          coordinators={event?.eventCoordinators}
          readonly={!canEditCoordinator}
          coordinatorType="eventEvent"
          label="Event Coordinators"
        />

        <EventCapacity
          eventStatus={event?.status}
          eventId={id}
          capacity={event?.capacity}
        />

        <PartsDate id={id} parts={event?.parts} eventStatus={event?.status} />

        <TimezoneSelector
          eventId={id}
          timezone={event?.timezone}
          eventStatus={event?.status}
        />

        <LanguageSelector
          eventId={id}
          language={event?.language}
          eventStatus={event?.status}
        />

        {!isVirtual && (
          <Providing
            childcareProvided={event?.childcareProvided}
            foodProvided={event?.foodProvided}
            id={id}
            eventStatus={event?.status}
          />
        )}

        <EventAttendance
          eventId={id}
          attendees={event?.attendees}
          requiresRegistration={event?.requiresRegistration}
        />
      </Flex>
    </BadgeWrapper>
  );
};

export default BasicInfo;
